import clsx from "clsx";
import { useEffect, useState } from "react";
import Button from "../button/Button";

const ticTicSound = new Audio();

export const SpinWheel = ({
  segments,
  onFinished,
  spins,
  maxSpins,
  primaryColor = "black",
  contrastColor = "white",
  buttonText = "Spin",
  isOnlyOnce = true,
  size = 170,
  upDuration = 300,
  downDuration = 600,
  fontFamily = "Arial",
  arrowLocation = "center",
  showTextOnSpin = false,
  isSpinSound = true,
}) => {
  const segmentTextArray = segments
    .map((segment) => segment.segmentText)
    .filter(Boolean);
  const segColorArray = segments
    .map((segment) => segment.segColor)
    .filter(Boolean);

  const [isFinished, setFinished] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [needleText, setNeedleText] = useState("");
  let currentSegment = "";

  let timerHandle = 0;
  const timerDelay = segmentTextArray.length;
  let angleCurrent = 0;
  let angleDelta = 0;

  let canvasContext = null;
  let maxSpeed = Math.PI / segmentTextArray.length;
  const upTime = segmentTextArray.length * upDuration;
  const downTime = segmentTextArray.length * downDuration;
  let spinStart = 0;

  let frames = 0;
  const centerX = size;
  const centerY = size;

  useEffect(() => {
    wheelInit();
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wheelDraw = () => {
    clear();
    drawWheel();
    drawNeedle();
  };

  const wheelInit = () => {
    initCanvas();
    wheelDraw();
  };

  const initCanvas = () => {
    let canvas = document.getElementById("canvas");

    if (!canvas) {
      // Create a new canvas if it doesn't exist
      canvas = document.createElement("canvas");
      canvas.setAttribute("width", `${size * 2}`);
      canvas.setAttribute("height", `${size * 2}`);
      canvas.setAttribute("id", "canvas");
      document?.getElementById("wheel")?.appendChild(canvas);
    }
    canvasContext = canvas.getContext("2d");

    // Enable high DPI rendering
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = size * 2 * devicePixelRatio;
    canvas.height = size * 2 * devicePixelRatio;
    canvas.style.width = size * 2 + "px";
    canvas.style.height = size * 2 + "px";
    canvasContext.scale(devicePixelRatio, devicePixelRatio);

    canvas.style.borderRadius = "50%"; // Set border radius for a circular shape
    // if (spins !== maxSpins) {
    //   canvas?.addEventListener("click", handleSpin, false);
    // }
  };

  const handleSpin = () => {
    if (spins >= maxSpins || isStarted) return;

    if (spins !== maxSpins) {
      if ((isFinished && isOnlyOnce) || isStarted) return; // Prevent spinning when disabled/already spinning
      spin(); // Call your existing spin function
    }
  };

  let targetFinalAngle = 0;

  const spin = () => {
    setIsStarted(true);
    if (timerHandle === 0) {
      spinStart = new Date().getTime();

      // Select target segment using probability
      const selectedIndex = selectSegmentByProbability();

      const targetIndex = segmentTextArray.length - selectedIndex - 1;
      const baseTargetAngle =
        (targetIndex / segmentTextArray.length) * (Math.PI * 2) - Math.PI / 2;

      // Add multiple full rotations + target angle
      const fullRotations = 3 + Math.random() * 2; // 3-5 full spins
      targetFinalAngle = Math.PI * 2 * fullRotations + baseTargetAngle;

      while (targetFinalAngle < 0) targetFinalAngle += Math.PI * 2;

      // Add some randomness to the max speed
      maxSpeed =
        (Math.PI / segmentTextArray.length) * (0.95 + Math.random() * 0.1);
      // Add randomness to the durations
      const randomizedUpTime = upTime * (0.8 + Math.random() * 0.4);
      const randomizedDownTime = downTime * (0.8 + Math.random() * 0.4);
      frames = 0;
      timerHandle = setInterval(
        () => onTimerTick(randomizedUpTime, randomizedDownTime),
        timerDelay * 5,
      );
    }
  };

  const selectSegmentByProbability = () => {
    // Check if segments have probability data
    const hasProb = segments.some(
      (segment) => segment.probability !== undefined,
    );

    if (!hasProb) {
      // Fallback to random selection
      return Math.floor(Math.random() * segmentTextArray.length);
    }

    const random = Math.random();
    let cumulativeProb = 0;

    for (let i = 0; i < segments.length; i++) {
      cumulativeProb += segments[i].probability || 0;
      if (random <= cumulativeProb) {
        return i;
      }
    }
    return segments.length - 1;
  };

  const onTimerTick = (randomizedUpTime, randomizedDownTime) => {
    frames++;
    wheelDraw();
    const duration = new Date().getTime() - spinStart;
    let progress = 0;
    let finished = false;

    if (duration < randomizedUpTime) {
      // Acceleration phase - build up speed
      progress = duration / randomizedUpTime;
      const spinSpeed = maxSpeed * Math.sin((progress * Math.PI) / 2) * 8; // Increased speed multiplier
      angleCurrent += spinSpeed;
    } else {
      // Deceleration phase - ease towards target
      const decelDuration = duration - randomizedUpTime;
      progress = decelDuration / randomizedDownTime;

      if (progress >= 1) {
        progress = 1;
        finished = true;
      }

      // Ease to target angle during deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
      const currentTarget = targetFinalAngle * easeProgress;
      angleCurrent = currentTarget;
    }

    while (angleCurrent >= Math.PI * 2) angleCurrent -= Math.PI * 2;

    if (finished) {
      setFinished(true);
      setIsStarted(false);

      wheelDraw(); // Final redraw

      // Get the segment the needle points to
      const change = angleCurrent + Math.PI / 2;
      let i =
        segmentTextArray.length -
        Math.floor((change / (Math.PI * 2)) * segmentTextArray.length) -
        1;
      if (i < 0) i = i + segmentTextArray.length;
      else if (i >= segmentTextArray.length) i = i - segmentTextArray.length;

      currentSegment = segmentTextArray[i];

      onFinished(currentSegment);
      clearInterval(timerHandle);
      timerHandle = 0;
      angleDelta = 0;
      ticTicSound.pause();
      ticTicSound.currentTime = 0;
    }
  };

  useEffect(() => {
    if (needleText && isSpinSound && isStarted) {
      try {
        ticTicSound.currentTime = 0;
        const playPromise = ticTicSound.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn("Audio interrupted", error);
          });
        }
      } catch (e) {
        console.warn("Audio play error", e);
      }
    } else {
      ticTicSound.pause();
      ticTicSound.currentTime = 0;
    }
  }, [needleText, isSpinSound, isStarted]);

  const [loadedImages, setLoadedImages] = useState({});

  const drawSegment = (key, lastAngle, angle) => {
    const ctx = canvasContext;
    const value = segmentTextArray[key];
    const segment = segments[key];

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, size, lastAngle, angle, false);
    ctx.lineTo(centerX, centerY);
    ctx.closePath();

    // Use segment's background color if available, fallback to segColorArray
    ctx.fillStyle = segment?.segColor || segColorArray[key] || "#E6E6E6";
    ctx.fill();
    ctx.stroke();

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((lastAngle + angle) / 2);

    // Use segment's text color if available, fallback to primary color
    ctx.fillStyle = segment?.textColor || primaryColor;
    ctx.font = "bold 9.7px " + fontFamily;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const textRadius = size * 0.6; // Optimal distance from center

    // Draw icon and text
    if (segment?.imageUrl && loadedImages[segment.imageUrl]) {
      const img = loadedImages[segment.imageUrl];
      const iconSize = 16;

      // Position icon and text vertically centered
      const iconY = -18; // Icon above text
      const textY = 8; // Text below icon
      const iconX = textRadius - iconSize / 2;

      ctx.drawImage(img, iconX, iconY, iconSize, iconSize);
      ctx.fillText(value.substring(0, 23), textRadius, textY);
    } else {
      // Just draw text centered
      ctx.fillText(value.substring(0, 23), textRadius, 0);
    }

    ctx.restore();
  };

  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      const imagesToLoad = segments
        .filter(
          (segment) => segment?.imageUrl && !loadedImages[segment.imageUrl],
        )
        .map((segment) => segment.imageUrl);

      if (imagesToLoad.length === 0) return;

      const imagePromises = imagesToLoad.map((imageUrl) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve({ url: imageUrl, img });
          img.onerror = () => resolve({ url: imageUrl, img: null });
          img.src = imageUrl;
        });
      });

      const results = await Promise.all(imagePromises);
      const newImages = {};
      results.forEach(({ url, img }) => {
        if (img) newImages[url] = img;
      });

      if (Object.keys(newImages).length > 0) {
        setLoadedImages((prev) => ({ ...prev, ...newImages }));
      }
    };

    loadImages();
  }, [segments]);

  // Redraw wheel when images are loaded
  useEffect(() => {
    if (canvasContext && Object.keys(loadedImages).length > 0) {
      setTimeout(() => {
        if (typeof wheelDraw === "function") {
          wheelDraw();
        }
      }, 100); // Small delay to ensure canvas is ready
    }
  }, [loadedImages]);

  const drawWheel = () => {
    const ctx = canvasContext;
    let lastAngle = angleCurrent;
    const len = segmentTextArray.length;
    const PI2 = Math.PI * 2;

    // ADD GLOW EFFECT
    ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;

    // DONE WITH GLOW EFFECT

    ctx.lineWidth = 1;
    ctx.strokeStyle = primaryColor;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = "0.7em " + fontFamily;
    for (let i = 1; i <= len; i++) {
      const angle = PI2 * (i / len) + angleCurrent;
      drawSegment(i - 1, lastAngle, angle);
      lastAngle = angle;
    }

    // Draw a center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 20, 0, PI2, false);
    ctx.closePath();
    ctx.fillStyle = primaryColor;
    ctx.lineWidth = 2;
    ctx.strokeStyle = contrastColor;
    ctx.fill();
    ctx.font = "bold 1em " + fontFamily;
    ctx.fillStyle = contrastColor;
    ctx.textAlign = "center";
    ctx.fillText(buttonText, centerX, centerY + 3);
    ctx.stroke();

    // Draw outer circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, size, 0, PI2, false);
    ctx.closePath();

    ctx.lineWidth = 4;
    ctx.strokeStyle = primaryColor;
    ctx.stroke();
  };

  const drawNeedle = () => {
    const ctx = canvasContext;
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#000";
    ctx.fileStyle = "#000";
    ctx.beginPath();

    ctx.fillStyle = primaryColor;
    ctx.strokeStyle = contrastColor;
    ctx.lineWidth = 2;
    if (arrowLocation === "top") {
      ctx.moveTo(centerX + 10, centerY / 15);
      ctx.lineTo(centerX - 10, centerY / 15);
      ctx.lineTo(centerX, centerY - centerY / 1.35);
    } else {
      ctx.moveTo(centerX + 6, centerY - 20);
      ctx.lineTo(centerX - 6, centerY - 20);
      ctx.lineTo(centerX, centerY - centerY / 3.8);
    }

    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    const change = angleCurrent + Math.PI / 2;
    let i =
      segmentTextArray.length -
      Math.floor((change / (Math.PI * 2)) * segmentTextArray.length) -
      1;
    if (i < 0) i = i + segmentTextArray.length;
    else if (i >= segmentTextArray.length) i = i - segmentTextArray.length;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#000";
    ctx.font = "bold 1.5em " + fontFamily;
    currentSegment = segmentTextArray[i];
    setNeedleText(segmentTextArray[i]);
  };

  const clear = () => {
    if (!canvasContext) {
      initCanvas(); // Reinitialize if context is lost
    }
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvasContext.clearRect(
      0,
      0,
      size * 2 * devicePixelRatio,
      size * 2 * devicePixelRatio,
    ); // Use full canvas size
  };

  return (
    <div id="wheel" className="flex justify-center items-center flex-col">
      <div className="border-white border-2 w-fit rounded-full">
        <canvas
          id="canvas"
          width={size * 2}
          height={size * 2}
          style={{
            pointerEvents: isFinished && isOnlyOnce ? "none" : "auto",
          }}
          onClick={handleSpin}
        />
      </div>
      <div className={clsx("px-5 mt-10 w-full")}>
        <Button
          className={clsx("!w-full", {
            "cursor-not-allowed": isStarted || spins >= maxSpins,
          })}
          text={spins >= maxSpins ? "No Spins Left" : "Spin"}
          disabled={isStarted || spins >= maxSpins}
          onClick={handleSpin}
        />
      </div>

      <div
        style={{
          textAlign: "center",
          padding: "10px 20px 0px",
          fontWeight: "bold",
          fontSize: "1.2em",
          fontFamily: fontFamily,
          minHeight: "40px",
        }}
        className={clsx("text-white transition-all duration-300", {
          "scale-110 text-blue-300": isStarted,
        })}
      >
        {needleText && isFinished && (
          <span className="inline-block animate-fade-in">{needleText}</span>
        )}
      </div>
    </div>
  );
};
